import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../services/api';

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [errorKind, setErrorKind] = useState('error'); // 'error' | 'warning'
  const [resetUrl, setResetUrl] = useState(null);
  const [darkMode, setDarkMode] = useState(true);
  const inputRef = useRef(null);

  // Foca o campo de email ao abrir
  useEffect(() => {
    if (!success && inputRef.current) {
      inputRef.current.focus();
    }
  }, [success]);

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResetUrl(null);

    if (!isValidEmail(email.trim())) {
      setErrorKind('error');
      setError('Digite um e-mail válido para continuar.');
      return;
    }

    setLoading(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 45000);

    try {
      const response = await fetch(`${API_BASE_URL}/forgot-password/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
        signal: controller.signal,
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setSubmittedEmail(email.trim());
        setSuccess(true);
        if (data.reset_url) setResetUrl(data.reset_url);
        if (data.email_error) {
          setErrorKind('warning');
          setError(
            'Solicitação registrada, mas o servidor não conseguiu enviar o e-mail. '
            + 'Avise o administrador.'
          );
        }
      } else {
        setErrorKind('error');
        setError(
          data.error
          || `Erro do servidor (HTTP ${response.status}). Tente novamente em instantes.`
        );
      }
    } catch (err) {
      setErrorKind('error');
      if (err.name === 'AbortError') {
        setError(
          'O servidor demorou demais para responder. Tente de novo em alguns instantes.'
        );
      } else {
        setError(
          'Não foi possível alcançar o servidor. Verifique sua conexão e tente novamente.'
        );
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  const handleTryAnother = () => {
    setSuccess(false);
    setError(null);
    setResetUrl(null);
    setEmail('');
    setSubmittedEmail('');
  };

  const currentYear = new Date().getFullYear();

  return (
    <div
      className={`min-h-screen flex items-center justify-center font-['Inter',sans-serif] px-4 ${
        darkMode ? 'text-slate-200' : 'text-slate-700'
      }`}
      style={{
        background: darkMode
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)'
          : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%)',
      }}
    >
      {/* Decorações de fundo */}
      <div
        className={`fixed w-[400px] h-[400px] bg-[#0974BA] rounded-full blur-[80px] -top-24 -right-24 -z-10 ${
          darkMode ? 'opacity-30' : 'opacity-20'
        }`}
      ></div>
      <div
        className={`fixed w-[300px] h-[300px] bg-[#2E4974] rounded-full blur-[80px] -bottom-12 -left-12 -z-10 ${
          darkMode ? 'opacity-30' : 'opacity-20'
        }`}
      ></div>

      {/* Botão de alternar tema */}
      <button
        type="button"
        onClick={() => setDarkMode(!darkMode)}
        aria-label={darkMode ? 'Ativar modo claro' : 'Ativar modo escuro'}
        className={`fixed top-5 right-5 w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 z-10 ${
          darkMode
            ? 'bg-slate-800/80 border border-slate-600/15 text-yellow-400 hover:bg-slate-700/80'
            : 'bg-white/80 border border-slate-200 text-slate-600 hover:bg-slate-100/80 shadow-lg'
        }`}
        title={darkMode ? 'Ativar modo claro' : 'Ativar modo escuro'}
      >
        {darkMode ? (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        )}
      </button>

      <div className="w-full max-w-[440px]">
        <div
          className={`relative backdrop-blur-xl border rounded-3xl px-8 py-10 sm:px-10 sm:py-12 overflow-hidden ${
            darkMode
              ? 'bg-slate-800/80 border-slate-600/15'
              : 'bg-white/90 border-slate-200 shadow-xl'
          }`}
        >
          {/* Barra gradiente no topo */}
          <div
            className="absolute top-0 left-0 right-0 h-1"
            style={{ background: 'linear-gradient(90deg, #0974BA, #2E4974)' }}
          ></div>

          {/* Logo + Título */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <img
                src="/iconSIDEB.svg"
                alt="SIDEB"
                className="w-full h-full object-contain"
              />
            </div>
            <div
              className={`text-3xl sm:text-4xl font-bold bg-clip-text text-transparent ${
                darkMode
                  ? 'bg-gradient-to-r from-gray-50 to-gray-400'
                  : 'bg-gradient-to-r from-slate-700 to-slate-500'
              }`}
            >
              SIDEB +
            </div>
            <div
              className={`text-sm mt-2 ${
                darkMode ? 'text-slate-400' : 'text-slate-500'
              }`}
            >
              {success ? 'Solicitação enviada' : 'Recuperação de senha'}
            </div>
          </div>

          {success ? (
            /* ============ ESTADO DE SUCESSO ============ */
            <div className="text-center">
              {/* Ícone verde de check */}
              <div
                className={`mx-auto mb-5 w-16 h-16 rounded-full flex items-center justify-center ${
                  darkMode ? 'bg-[#13983D]/15' : 'bg-[#13983D]/10'
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-9 w-9 text-[#13983D]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <h2
                className={`text-xl font-semibold mb-2 ${
                  darkMode ? 'text-slate-100' : 'text-slate-800'
                }`}
              >
                Verifique seu e-mail
              </h2>

              <p
                className={`text-sm mb-1 ${
                  darkMode ? 'text-slate-400' : 'text-slate-600'
                }`}
              >
                Se o e-mail estiver cadastrado, enviamos um link de redefinição para:
              </p>
              <p
                className={`text-sm font-semibold mb-5 break-all ${
                  darkMode ? 'text-[#0974BA]' : 'text-[#0974BA]'
                }`}
              >
                {submittedEmail}
              </p>

              {/* Aviso sobre spam */}
              <div
                className={`mb-5 p-3 rounded-xl text-xs flex items-start gap-2 text-left ${
                  darkMode
                    ? 'bg-slate-900/50 border border-slate-700/30 text-slate-400'
                    : 'bg-slate-50 border border-slate-200 text-slate-600'
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#F9B047]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>
                  Pode levar alguns minutos para chegar. Não esqueça de conferir as
                  pastas <strong>Promoções</strong> e <strong>Spam</strong>. O link
                  expira em 1 hora.
                </span>
              </div>

              {/* Aviso de erro de envio (se houver) */}
              {error && errorKind === 'warning' && (
                <div
                  className={`mb-5 p-3 rounded-xl text-xs text-left flex items-start gap-2 ${
                    darkMode
                      ? 'bg-[#F9B047]/10 border border-[#F9B047]/30 text-[#F9B047]'
                      : 'bg-[#F9B047]/10 border border-[#F9B047]/30 text-[#9A6B16]'
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 flex-shrink-0 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              {/* Link de reset em desenvolvimento */}
              {resetUrl && (
                <div
                  className={`mb-5 p-4 rounded-xl text-sm text-left ${
                    darkMode
                      ? 'bg-[#F9B047]/10 border border-[#F9B047]/30'
                      : 'bg-[#F9B047]/10 border border-[#F9B047]/20'
                  }`}
                >
                  <p
                    className={`text-xs mb-2 ${
                      darkMode ? 'text-slate-400' : 'text-slate-500'
                    }`}
                  >
                    Modo desenvolvimento — clique para abrir:
                  </p>
                  <a
                    href={resetUrl}
                    className="text-[#F9B047] text-sm font-medium hover:text-[#0974BA] transition-colors break-all"
                  >
                    Redefinir minha senha
                  </a>
                </div>
              )}

              {/* Ações */}
              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  onClick={handleTryAnother}
                  className={`w-full rounded-xl py-3 text-sm font-medium transition-all duration-300 ${
                    darkMode
                      ? 'bg-slate-700/50 text-slate-200 border border-slate-600/30 hover:bg-slate-700'
                      : 'bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200'
                  }`}
                >
                  Tentar com outro e-mail
                </button>
                <Link
                  to="/login"
                  className="text-sm text-[#0974BA] hover:text-[#F9B047] transition-colors"
                >
                  &larr; Voltar para o login
                </Link>
              </div>
            </div>
          ) : (
            /* ============ FORMULÁRIO ============ */
            <>
              {error && errorKind === 'error' && (
                <div
                  role="alert"
                  className={`mb-5 p-4 border rounded-xl text-sm flex items-start gap-2 ${
                    darkMode
                      ? 'bg-[#E83A3C]/10 border-[#E83A3C]/30 text-[#E83A3C]'
                      : 'bg-[#E83A3C]/10 border-[#E83A3C]/20 text-[#E83A3C]'
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 flex-shrink-0 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              <p
                className={`text-sm text-center mb-6 ${
                  darkMode ? 'text-slate-400' : 'text-slate-500'
                }`}
              >
                Digite seu e-mail cadastrado e enviaremos um link para redefinir
                sua senha.
              </p>

              <form onSubmit={handleSubmit} noValidate>
                <div className="mb-5">
                  <label
                    htmlFor="email"
                    className={`block text-xs font-medium mb-2 uppercase tracking-wide ${
                      darkMode ? 'text-slate-400' : 'text-slate-500'
                    }`}
                  >
                    E-mail
                  </label>
                  <div className="relative">
                    {/* Ícone do envelope */}
                    <div
                      className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none ${
                        darkMode ? 'text-slate-500' : 'text-slate-400'
                      }`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-2 11H5a2 2 0 01-2-2V7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <input
                      ref={inputRef}
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="seu@email.com.br"
                      aria-invalid={!!error && errorKind === 'error'}
                      className={`w-full border rounded-xl pl-12 pr-4 py-3.5 text-sm transition-all duration-300 focus:outline-none focus:border-[#0974BA] focus:ring-[3px] focus:ring-[#0974BA]/20 ${
                        darkMode
                          ? 'bg-slate-900/80 border-slate-600/20 text-slate-200 placeholder-slate-600'
                          : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
                      }`}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full rounded-xl py-4 text-white font-semibold text-base transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_40px_rgba(101,140,191,0.4)] active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none"
                  style={{ background: 'linear-gradient(135deg, #0974BA, #2E4974)' }}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Enviando...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Enviar instruções
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </span>
                  )}
                </button>
              </form>

              <div className="text-center mt-6">
                <Link
                  to="/login"
                  className={`text-sm transition-colors inline-flex items-center gap-2 ${
                    darkMode
                      ? 'text-slate-400 hover:text-slate-200'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Voltar para o login
                </Link>
              </div>
            </>
          )}

          {/* Footer */}
          <p
            className={`text-center mt-8 text-xs ${
              darkMode ? 'text-slate-600' : 'text-slate-400'
            }`}
          >
            © {currentYear} SIDEB — Secretaria Municipal de Educação
          </p>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
